import { money } from './money.js';
import { apportionByValue } from './order/apportion.js';
/**
 * Staff commission. §6.24 `COM-001` to `COM-009`, flow §15.15.
 *
 * `COM-008` says disputes about commission destroy trust in the whole system, so everything here is
 * built to be explainable line by line: nothing is stored as a bare total. The unit of record is an
 * append-only `CommissionEntry` (one per line per credited person, plus one per clawback, remake and
 * metric bonus), and a statement is just those entries summed. Nothing is edited or deleted.
 *
 * §15.15's failure branch resolves a dispute with a correction under §6.33 `COR-001`, which
 * *reassigns* the line. There is no entry kind for that: taking a line off one person and crediting
 * another is not an earning, a clawback, a remake or a bonus, and the earned entry id is derived
 * from the line and the person, so re-evaluating the line refuses as a double posting. A dispute
 * can be reported against this module's entries but not resolved through it.
 *
 * ---
 *
 * **Rounding.** A percentage applied to minor units yields a fraction of a paisa. This module
 * resolves that fraction once per line, using the scheme's own mode. It is not one of `MR-04`'s
 * three rounding points (steps 8, 11, 12), all of which are on the price chain in `MR-03`.
 * Commission is a derived payroll figure that never feeds a price, a tax line or a tender, so it
 * cannot shift any customer-facing amount; but it follows the same discipline: never round
 * mid-calculation. The rounding mode is not in the spec, and Appendix D names none, so it is a
 * scheme parameter with no default.
 *
 * **Apportionment.** Splits (`COM-003`) divide the line's *base* across the credited staff using
 * `apportionByValue`, so the base shares sum to the base exactly. The rate is then applied to the
 * line as a whole and the result apportioned back by each person's base share times their own rate
 * (`creditAmounts`), so a split line costs the shop what the same line sold by one person costs, and
 * a per-person tier can still rate two people on one line differently. A clawback mirrors it: the
 * proportion is taken from the line's commission once and apportioned across what each person still
 * holds. Only the clawback rounds a figure that was already rounded, because it must reverse the
 * amount that was paid rather than recompute it.
 *
 * **Percentages** are `numeric(6,3)` per Appendix H, held as integer thousandths of a percent:
 * 2.5% is `2500n`, 100% is `100000n`.
 */
export class CommissionError extends Error {
}
/** 100% in thousandths of a percent (Appendix H `numeric(6,3)`). */
export const PERCENT_SCALE = 100000n;
/** `numeric(6,3)` holds at most 999.999. */
export const MAX_PERCENT_MILLI = 999999n;
// ---------------------------------------------------------------------------------------------
// Scheme
// ---------------------------------------------------------------------------------------------
/** `COM-003`'s four roles, in the spec's own resolution order. */
export const ATTRIBUTION_ROLES = [
    'dispense_creator',
    'payment_taker',
    'fitter',
    'handover',
];
/**
 * `COM-003`: "in resolution order: the staff member who created the dispense, who took payment, who
 * fitted, who handed over." The tenant setting is the order actually used, so a tenant may pass a
 * different one; this is only the spec's order, offered so nobody retypes it.
 */
export const DEFAULT_ATTRIBUTION_ORDER = ATTRIBUTION_ROLES;
/**
 * `COM-005`'s five behaviour metrics. How each is counted (what is a "second pair", what is an
 * "upgrade") is not specified, so this module never counts one: it is given the reading.
 */
export const COMMISSION_METRICS = [
    'second_pair_attach',
    'lens_tier_upgrade',
    'coating_attach',
    'contact_lens_conversion',
    'annual_supply_conversion',
];
// ---------------------------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------------------------
/**
 * `value * num / den`, rounded once. The only place a fraction of a minor unit is resolved.
 * `half_up` is half away from zero; `down` is toward zero. `den` must be positive.
 *
 * The sign comes from the product, not from `value` alone: both halves of the formula below need a
 * non-negative magnitude, and a negative `num` would otherwise round toward positive infinity.
 */
export function scaleMinor(value, num, den, mode) {
    if (den <= 0n)
        throw new CommissionError('Denominator must be positive');
    const negative = value < 0n !== num < 0n; // one of the two, not both
    const magnitude = (value < 0n ? -value : value) * (num < 0n ? -num : num);
    const q = mode === 'down' ? magnitude / den : (2n * magnitude + den) / (2n * den);
    return negative ? -q : q;
}
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
function assertDate(label, value) {
    if (!ISO_DATE.test(value)) {
        throw new CommissionError(`${label} must be an ISO date (YYYY-MM-DD), got ${JSON.stringify(value)}`);
    }
}
function inPeriod(period, date) {
    return date >= period.start && date <= period.end;
}
function assertCurrency(scheme, m, label) {
    if (m.currency !== scheme.currency) {
        throw new CommissionError(`${label} is ${m.currency}, scheme is ${scheme.currency}`);
    }
}
function assertRate(label, rate) {
    if (rate < 0n || rate > MAX_PERCENT_MILLI) {
        throw new CommissionError(`${label} must be within 0 and 999.999 percent (numeric(6,3)), got ${rate.toString()}`);
    }
}
// ---------------------------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------------------------
function usesMargin(scheme) {
    return (Object.values(scheme.rules).some((r) => r.basis === 'margin') ||
        scheme.tier?.measure === 'margin');
}
/** Throws on any scheme the engine could not evaluate faithfully. Exported so a settings screen can call it on save. */
export function validateScheme(scheme, holdsCostPrice) {
    money(0n, scheme.currency);
    if (usesMargin(scheme) && !holdsCostPrice) {
        throw new CommissionError('COM-002: a margin-based scheme needs cost visibility, so it requires the cost-price permission');
    }
    if (scheme.attributionOrder.length === 0) {
        throw new CommissionError('COM-003: attributionOrder must name at least one role');
    }
    if (new Set(scheme.attributionOrder).size !== scheme.attributionOrder.length) {
        throw new CommissionError('COM-003: attributionOrder lists a role twice');
    }
    for (const [kind, rule] of Object.entries(scheme.rules)) {
        if (rule.basis === 'flat') {
            assertCurrency(scheme, rule.amount, `Flat amount for ${kind}`);
            if (rule.amount.minor < 0n)
                throw new CommissionError(`Flat amount for ${kind} is negative`);
        }
        else if (rule.basis === 'lens_tier') {
            for (const [tier, amount] of Object.entries(rule.amountByTier)) {
                assertCurrency(scheme, amount, `Lens tier ${tier}`);
                if (amount.minor < 0n)
                    throw new CommissionError(`Lens tier ${tier} amount is negative`);
            }
        }
        else {
            assertRate(`Rate for ${kind}`, rule.rateMilliPercent);
        }
    }
    const tier = scheme.tier;
    if (tier) {
        if (tier.steps.length === 0)
            throw new CommissionError('COM-004: a tier needs at least one step');
        let previous;
        for (const step of tier.steps) {
            assertRate('Tier rate', step.rateMilliPercent);
            if (step.thresholdMinor < 0n)
                throw new CommissionError('COM-004: threshold is negative');
            if (previous !== undefined && step.thresholdMinor <= previous) {
                throw new CommissionError('COM-004: tier thresholds must be strictly increasing');
            }
            previous = step.thresholdMinor;
        }
    }
    const ruledMetrics = new Set();
    for (const rule of scheme.metricRules ?? []) {
        assertCurrency(scheme, rule.bonus, `Bonus for ${rule.metric}`);
        if (rule.thresholdDenominator <= 0n || rule.thresholdNumerator < 0n) {
            throw new CommissionError(`COM-005: threshold for ${rule.metric} must be a non-negative ratio`);
        }
        // A ladder of bonuses on one metric is not in COM-005, and whether two cleared thresholds both
        // pay or only the higher one does is the tenant's money either way. Refused here rather than at
        // evaluation, where it surfaced as a double-posting error against a scheme that was accepted.
        if (ruledMetrics.has(rule.metric)) {
            throw new CommissionError(`COM-005: two rules for ${rule.metric}; one rule per metric, because the spec does not say whether bonuses stack`);
        }
        ruledMetrics.add(rule.metric);
    }
    if (scheme.remake.deduction.kind === 'fixed') {
        assertCurrency(scheme, scheme.remake.deduction.amount, 'Remake deduction');
        if (scheme.remake.deduction.amount.minor < 0n) {
            throw new CommissionError('COM-007: remake deduction is negative; give it as a positive amount');
        }
    }
}
function validateLine(line) {
    assertDate(`${line.orderLineId} soldOn`, line.soldOn);
    if (line.settledOn !== undefined)
        assertDate(`${line.orderLineId} settledOn`, line.settledOn);
    if (line.netMinor < 0n || line.listMinor < 0n) {
        throw new CommissionError(`${line.orderLineId}: a sale line cannot be negative; returns arrive as clawback events`);
    }
    if (line.netMinor > line.listMinor) {
        throw new CommissionError(`${line.orderLineId}: net exceeds list, which a discount cannot do`);
    }
}
function resolveCredits(line, order) {
    if (line.split !== undefined) {
        if (line.split.length === 0)
            throw new CommissionError(`${line.orderLineId}: split is empty`);
        const ids = new Set();
        let total = 0n;
        for (const share of line.split) {
            if (share.percentMilli <= 0n || share.percentMilli > PERCENT_SCALE) {
                throw new CommissionError(`${line.orderLineId}: a split share must be above 0 and at most 100%`);
            }
            if (ids.has(share.staffId)) {
                throw new CommissionError(`${line.orderLineId}: ${share.staffId} appears twice in the split`);
            }
            ids.add(share.staffId);
            total += share.percentMilli;
        }
        if (total !== PERCENT_SCALE) {
            throw new CommissionError(`${line.orderLineId}: split totals ${total.toString()} thousandths of a percent, not 100%`);
        }
        return line.split.map((s) => ({
            staffId: s.staffId,
            via: 'split',
            percentMilli: s.percentMilli,
        }));
    }
    for (const role of order) {
        const staffId = line.staff[role];
        if (staffId !== undefined)
            return [{ staffId, via: role, percentMilli: PERCENT_SCALE }];
    }
    throw new CommissionError(`COM-003: ${line.orderLineId} has nobody in any of ${order.join(', ')}, so nobody can be credited`);
}
function lineMeasure(line, measure) {
    if (measure === 'retail')
        return line.netMinor;
    if (line.costMinor === undefined) {
        throw new CommissionError(`COM-002: ${line.orderLineId} has no cost, so margin cannot be measured`);
    }
    return line.netMinor - line.costMinor;
}
function splitByCredit(total, credits) {
    return apportionByValue(total, credits.map((c, i) => ({ id: String(i), valueMinor: c.percentMilli }))).map((s) => s.shareMinor);
}
/**
 * What each credited person earns on one line.
 *
 * A rule with no rate (`flat`, `lens_tier`) pays the base share itself and resolves no fraction. A
 * rate is applied to the **line** once: the weights are each person's base share times their own
 * rate, the line's commission is one rounding of their sum, and `apportionByValue` splits it back by
 * those weights. Rounding each person's share on its own instead would make a split line cost the
 * shop more than the same line sold by one person: at 5% of 100 paisa, a ten-way split paid 10 where
 * the line earns 5. Weights, not percentages, so a person tier that rates two people on one line
 * differently still gets one rounding.
 */
function creditAmounts(baseShares, rates, mode) {
    if (!rates.some((r) => r !== undefined))
        return [...baseShares];
    const weights = baseShares.map((base, i) => base * (rates[i] ?? 0n));
    const total = scaleMinor(weights.reduce((s, w) => s + w, 0n), 1n, PERCENT_SCALE, mode);
    return apportionByValue(total, weights.map((w, i) => ({ id: String(i), valueMinor: w }))).map((s) => s.shareMinor);
}
function ruleBase(line, rule) {
    switch (rule.basis) {
        case 'flat':
            return rule.amount.minor;
        case 'lens_tier': {
            const amount = line.lensTier === undefined ? undefined : rule.amountByTier[line.lensTier];
            if (amount === undefined) {
                throw new CommissionError(`COM-002: ${line.orderLineId} has lens tier ${JSON.stringify(line.lensTier)}, which the scheme does not price`);
            }
            return amount.minor;
        }
        case 'retail':
            return rule.base === 'list' ? line.listMinor : line.netMinor;
        case 'margin': {
            const margin = lineMeasure(line, 'margin');
            if (margin < 0n) {
                if (rule.negativeMargin === 'refuse') {
                    throw new CommissionError(`${line.orderLineId} sold below cost and the scheme refuses negative margin`);
                }
                return 0n;
            }
            return margin;
        }
    }
}
function pickStep(tier, measure) {
    let chosen;
    for (const step of tier.steps) {
        if (step.inclusive ? measure >= step.thresholdMinor : measure > step.thresholdMinor)
            chosen = step;
    }
    return chosen;
}
/**
 * What a person still holds on a line: earned less clawbacks and deductions, never below zero.
 *
 * The floor matters. Two fixed remake deductions can exceed what the person earned on the line, and
 * without it a `line_commission` deduction would pay them the difference and a later refund would
 * reverse a negative amount, which is a credit.
 */
function remainingOnLine(entries, orderLineId, staffId) {
    let sum = 0n;
    for (const e of entries) {
        if (e.staffId !== staffId || e.kind === 'metric_bonus')
            continue;
        if (e.orderLineId === orderLineId)
            sum += e.amount.minor;
    }
    return sum > 0n ? sum : 0n;
}
/** The highest cumulative refund already recorded against a line, in minor units of its net. */
function reversedSoFar(entries, orderLineId) {
    return entries
        .filter((e) => e.kind === 'clawback' && e.orderLineId === orderLineId)
        .reduce((m, e) => (e.cumulativeRefundedMinor > m ? e.cumulativeRefundedMinor : m), 0n);
}
/**
 * The net a clawback is a proportion of: the figure the commission was earned against, from the
 * earned entry of an earlier period, or from the line itself when it is earning in this one.
 * `undefined` when nothing earned on that line, which is an unmatched clawback.
 */
function lineNetFor(orderLineId, prior, plans) {
    const earned = prior.find((e) => e.kind === 'earned' && e.orderLineId === orderLineId);
    if (earned !== undefined)
        return earned.lineNetMinor;
    return plans.find((p) => p.line.orderLineId === orderLineId)?.line.netMinor;
}
export function evaluatePeriod(input) {
    const { scheme, period } = input;
    validateScheme(scheme, input.holdsCostPrice);
    assertDate('period start', period.start);
    assertDate('period end', period.end);
    if (period.start > period.end)
        throw new CommissionError('Period ends before it starts');
    if (period.status !== 'open') {
        // COM-006: a closed period is never reopened, so nothing may post into it.
        throw new CommissionError(`Period ${period.id} is ${period.status}; only an open period accepts postings`);
    }
    const prior = input.priorEntries ?? [];
    // Prior entries are summed into this period's arithmetic and reversed in this period's currency,
    // so a foreign one would be reversed as though its minor units were the scheme's.
    for (const e of prior)
        assertCurrency(scheme, e.amount, `Prior entry ${e.entryId}`);
    const posted = new Set(prior.map((e) => e.entryId));
    const created = [];
    const post = (entry) => {
        if (posted.has(entry.entryId)) {
            throw new CommissionError(`Entry ${entry.entryId} is already posted; evaluating the same source twice would double-pay`);
        }
        posted.add(entry.entryId);
        created.push(entry);
    };
    const all = () => [...prior, ...created];
    const cur = scheme.currency;
    // 1. Which lines accrue in this period.
    const accruing = [];
    const deferredLines = [];
    const unruledLines = [];
    const lineIds = new Set();
    for (const line of input.lines) {
        validateLine(line);
        if (lineIds.has(line.orderLineId))
            throw new CommissionError(`${line.orderLineId} is listed twice`);
        lineIds.add(line.orderLineId);
        const accrual = scheme.earnsOn === 'sale' ? line.soldOn : line.settledOn;
        if (accrual === undefined) {
            deferredLines.push(line.orderLineId);
            continue;
        }
        if (!inPeriod(period, accrual)) {
            throw new CommissionError(`${line.orderLineId} accrues on ${accrual}, outside period ${period.start} to ${period.end}`);
        }
        accruing.push(line);
    }
    // 2. Rules and credits, then the base each credited person is paid on.
    const plans = [];
    for (const line of accruing) {
        const rule = scheme.rules[line.kind];
        if (rule === undefined || line.netMinor === 0n) {
            // A zero-value line has no refund ratio (a clawback divides by net) and nothing to pay on.
            unruledLines.push(line.orderLineId);
            continue;
        }
        const credits = resolveCredits(line, scheme.attributionOrder);
        plans.push({ line, rule, credits, baseShares: splitByCredit(ruleBase(line, rule), credits) });
    }
    // 3. Clawback events, checked before anything reads them. The tier measure below needs this
    //    period's refunds per line, and must not be computed from a total whose bound is only checked
    //    afterwards: that is how a clamp nothing can trigger ends up standing in for a guard.
    const refundedByLine = new Map();
    for (const ev of input.clawbacks ?? []) {
        assertDate(`${ev.eventId} discoveredOn`, ev.discoveredOn);
        if (!inPeriod(period, ev.discoveredOn)) {
            throw new CommissionError(`COM-006: ${ev.eventId} was discovered on ${ev.discoveredOn}, outside open period ${period.start} to ${period.end}; it cannot post into a closed period`);
        }
        if (ev.amountMinor <= 0n)
            throw new CommissionError(`${ev.eventId}: amount must be positive`);
        const total = (refundedByLine.get(ev.orderLineId) ?? 0n) + ev.amountMinor;
        refundedByLine.set(ev.orderLineId, total);
        const lineNet = lineNetFor(ev.orderLineId, prior, plans);
        if (lineNet !== undefined) {
            const cumulative = reversedSoFar(prior, ev.orderLineId) + total;
            if (cumulative > lineNet) {
                throw new CommissionError(`${ev.eventId}: ${cumulative.toString()} reversed against a line worth ${lineNet.toString()}; a line cannot be refunded for more than it sold for`);
            }
        }
    }
    // 4. Tier measures. Refunds discovered this period on lines sold this period reduce the measure,
    //    otherwise a sale and its refund in one month would still clear a threshold. The surviving
    //    proportion is taken with `down`, so a measure is never rounded up over a threshold. Measure
    //    arithmetic is never posted.
    const tier = scheme.tier;
    const personMeasure = new Map();
    const branchMeasure = new Map();
    if (tier) {
        for (const plan of plans) {
            const measure = lineMeasure(plan.line, tier.measure);
            const surviving = plan.line.netMinor - (refundedByLine.get(plan.line.orderLineId) ?? 0n);
            const net = (v) => scaleMinor(v, surviving, plan.line.netMinor, 'down');
            branchMeasure.set(plan.line.branchId, (branchMeasure.get(plan.line.branchId) ?? 0n) + net(measure));
            const shares = splitByCredit(measure, plan.credits);
            plan.credits.forEach((c, i) => {
                personMeasure.set(c.staffId, (personMeasure.get(c.staffId) ?? 0n) + net(shares[i] ?? 0n));
            });
        }
    }
    // 5. Earned entries (COM-001: one per line per credited person).
    for (const plan of plans) {
        const applied = plan.credits.map((credit) => {
            const rule = plan.rule;
            if (rule.basis !== 'retail' && rule.basis !== 'margin')
                return { tier: false };
            if (tier === undefined)
                return { rate: rule.rateMilliPercent, tier: false };
            const measure = tier.scope === 'person'
                ? (personMeasure.get(credit.staffId) ?? 0n)
                : (branchMeasure.get(plan.line.branchId) ?? 0n);
            const step = pickStep(tier, measure);
            return step === undefined
                ? { rate: rule.rateMilliPercent, tier: false }
                : { rate: step.rateMilliPercent, tier: true };
        });
        const amounts = creditAmounts(plan.baseShares, applied.map((a) => a.rate), scheme.rounding);
        const lineCommissionMinor = amounts.reduce((s, a) => s + a, 0n);
        plan.credits.forEach((credit, i) => {
            const rate = applied[i]?.rate;
            post({
                kind: 'earned',
                entryId: `earned:${plan.line.orderLineId}:${credit.staffId}`,
                periodId: period.id,
                branchId: plan.line.branchId,
                staffId: credit.staffId,
                amount: money(amounts[i] ?? 0n, cur),
                orderLineId: plan.line.orderLineId,
                lineKind: plan.line.kind,
                basis: plan.rule.basis,
                baseMinor: plan.baseShares[i] ?? 0n,
                lineCommissionMinor,
                ...(rate === undefined ? {} : { rateMilliPercent: rate }),
                tierApplied: applied[i]?.tier ?? false,
                creditedVia: credit.via,
                creditPercentMilli: credit.percentMilli,
                lineNetMinor: plan.line.netMinor,
            });
        });
    }
    // 6. Clawbacks (COM-006). Reverse the original entries in proportion, in this period.
    const unmatchedClawbacks = [];
    for (const ev of input.clawbacks ?? []) {
        const current = all();
        const earned = current.filter((e) => e.kind === 'earned' && e.orderLineId === ev.orderLineId);
        const first = earned[0];
        if (first === undefined) {
            unmatchedClawbacks.push(ev.eventId);
            continue;
        }
        const lineNet = first.lineNetMinor;
        const already = reversedSoFar(current, ev.orderLineId);
        const cumulative = already + ev.amountMinor;
        // The proportion is taken from the *line's* commission, rounded once, and split by what each
        // person earned on it. Rating each person's entry separately would claw back more than the line
        // earned, the mirror of the split over-pay `creditAmounts` avoids: a 50% refund on a ten-way
        // split of 100 paisa took back all 10 paisa of a 5 paisa commission.
        //
        // The target is cumulative and each person's own, not a share of this event's step. A step
        // shared out by what people still hold strands commission when one of them is capped: a remake
        // takes one person's half, their uncapped weight keeps attracting the step, and it is dropped
        // again, so a fully refunded line stays part paid. A cumulative target also makes the series
        // independent of a rounding mode that changed between tranches (`MR-09`).
        const lineEarned = earned.reduce((s, e) => s + e.amount.minor, 0n);
        const takenFrom = (e) => current
            .filter((c) => c.kind === 'clawback' && c.reversesEntryId === e.entryId)
            .reduce((s, c) => s - c.amount.minor, 0n);
        const targets = apportionByValue(scaleMinor(lineEarned, cumulative, lineNet, scheme.rounding), earned.map((e, i) => ({ id: String(i), valueMinor: e.amount.minor }))).map((s) => s.shareMinor);
        earned.forEach((e, i) => {
            const remaining = remainingOnLine(current, e.orderLineId, e.staffId);
            let tranche = (targets[i] ?? 0n) - takenFrom(e);
            // Largest-remainder is not monotone per person as the total grows, so one more paisa refunded
            // can move a unit between two people and leave a target a unit below what that person has
            // already had reversed. Reverse nothing rather than pay it back: this period then reverses a
            // paisa more than the line's own step, and the next tranche absorbs it. At a full refund every
            // target is exactly what that person earned, so the series still ends exactly reversed.
            if (tranche < 0n)
                tranche = 0n;
            // Never reverse more than the person still holds on this line (a remake deduction may have
            // taken some already).
            if (tranche > remaining)
                tranche = remaining;
            post({
                kind: 'clawback',
                entryId: `clawback:${ev.eventId}:${e.staffId}`,
                periodId: period.id,
                branchId: e.branchId,
                staffId: e.staffId,
                amount: money(-tranche, cur),
                orderLineId: e.orderLineId,
                eventId: ev.eventId,
                eventKind: ev.kind,
                reversesEntryId: e.entryId,
                originPeriodId: e.periodId,
                reversesEarlierPeriod: e.periodId !== period.id,
                refundedMinor: ev.amountMinor,
                cumulativeRefundedMinor: cumulative,
                lineNetMinor: lineNet,
            });
        });
    }
    // 7. Remakes (COM-007): always visible for the two shop-error causes, reducing only if configured.
    const visible = new Set(['shop_rx_error', 'shop_measurement_error']);
    for (const c of scheme.remake.reducingCauses)
        visible.add(c);
    for (const ev of input.remakes ?? []) {
        assertDate(`${ev.remakeId} discoveredOn`, ev.discoveredOn);
        if (!inPeriod(period, ev.discoveredOn)) {
            throw new CommissionError(`COM-007: ${ev.remakeId} was discovered on ${ev.discoveredOn}, outside open period ${period.id}`);
        }
        if (!visible.has(ev.cause))
            continue;
        const deduction = scheme.remake.deduction;
        let amount = 0n;
        if (scheme.remake.reducingCauses.includes(ev.cause)) {
            if (deduction.kind === 'fixed')
                amount = -deduction.amount.minor;
            else if (deduction.kind === 'line_commission') {
                amount = -remainingOnLine(all(), ev.orderLineId, ev.staffId);
            }
        }
        post({
            kind: 'remake_deduction',
            entryId: `remake:${ev.remakeId}:${ev.staffId}`,
            periodId: period.id,
            branchId: ev.branchId,
            staffId: ev.staffId,
            amount: money(amount, cur),
            orderLineId: ev.orderLineId,
            remakeId: ev.remakeId,
            cause: ev.cause,
            reduces: amount !== 0n,
        });
    }
    // 8. Behaviour bonuses (COM-005), from readings the caller measured. One reading per person per
    //    metric: two of them are two measurements of the same thing, and which one counts (or whether
    //    they add, for a person who worked in two branches) is not something COM-005 says.
    const readingSeen = new Set();
    for (const reading of input.metrics ?? []) {
        const key = `${reading.metric}:${reading.staffId}`;
        if (readingSeen.has(key)) {
            throw new CommissionError(`COM-005: two readings of ${reading.metric} for ${reading.staffId}; a metric is one reading per person per period`);
        }
        readingSeen.add(key);
        if (reading.denominator < 0n ||
            reading.numerator < 0n ||
            reading.numerator > reading.denominator) {
            throw new CommissionError(`COM-005: reading for ${reading.metric} must satisfy 0 <= numerator <= denominator`);
        }
        for (const rule of (scheme.metricRules ?? []).filter((r) => r.metric === reading.metric)) {
            if (reading.denominator === 0n)
                continue; // no opportunities, no rate to pay on
            const lhs = reading.numerator * rule.thresholdDenominator;
            const rhs = rule.thresholdNumerator * reading.denominator;
            if (rule.inclusive ? lhs >= rhs : lhs > rhs) {
                post({
                    kind: 'metric_bonus',
                    entryId: `metric:${period.id}:${reading.metric}:${reading.staffId}`,
                    periodId: period.id,
                    branchId: reading.branchId,
                    staffId: reading.staffId,
                    amount: rule.bonus,
                    metric: reading.metric,
                    numerator: reading.numerator,
                    denominator: reading.denominator,
                });
            }
        }
    }
    return {
        entries: created,
        statements: buildStatements(period.id, cur, created),
        deferredLines,
        unruledLines,
        unmatchedClawbacks,
    };
}
/** `COM-008`: a statement is the entries and their sums, so the working is the record. */
export function buildStatements(periodId, currency, entries) {
    const byStaff = new Map();
    for (const e of entries) {
        // Totals are summed as raw minor units, which is the one thing `money.ts` refuses to do across
        // currencies, so the check it would have made is made here.
        if (e.amount.currency !== currency) {
            throw new CommissionError(`Entry ${e.entryId} is ${e.amount.currency}, but this statement is in ${currency}`);
        }
        const list = byStaff.get(e.staffId) ?? [];
        list.push(e);
        byStaff.set(e.staffId, list);
    }
    return [...byStaff.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([staffId, list]) => {
        const sum = (k) => list.filter((e) => e.kind === k).reduce((s, e) => s + e.amount.minor, 0n);
        const net = list.reduce((s, e) => s + e.amount.minor, 0n);
        return {
            staffId,
            periodId,
            entries: list,
            earned: money(sum('earned'), currency),
            clawback: money(sum('clawback'), currency),
            remakeDeduction: money(sum('remake_deduction'), currency),
            metricBonus: money(sum('metric_bonus'), currency),
            net: money(net, currency),
            shortfall: money(net < 0n ? -net : 0n, currency),
        };
    });
}
// ---------------------------------------------------------------------------------------------
// Period lifecycle and payroll
// ---------------------------------------------------------------------------------------------
/** Evaluated and awaiting the owner. */
export function closePeriod(period) {
    if (period.status !== 'open') {
        throw new CommissionError(`Period ${period.id} is already ${period.status}`);
    }
    return { ...period, status: 'closed' };
}
/**
 * §15.15 step 5, the gate. `commission_view_all_approve` is held by Owner and Accountant
 * (Appendix B); the caller resolves that and passes the answer.
 */
export function approvePeriod(period, approverMayApprove) {
    if (!approverMayApprove) {
        throw new CommissionError('Approving commission needs the view-all-and-approve permission');
    }
    if (period.status !== 'closed') {
        throw new CommissionError(`Period ${period.id} is ${period.status}; only a closed period can be approved`);
    }
    return { ...period, status: 'approved' };
}
export const PAYROLL_FIELDS = [
    'staff',
    'period',
    'currency',
    'earned',
    'clawback',
    'remake_deduction',
    'metric_bonus',
    'net',
    'payable',
    'shortfall',
];
/**
 * `COM-009`. The payroll system's column names and file format are not in the spec, so the caller
 * maps each field to its own header and serialises the rows itself. Amounts are signed integer
 * minor units as strings, because a decimal would need a currency exponent this module has no
 * business knowing. Refuses before approval, because commission is not payable until the owner has
 * signed it off.
 *
 * Appendix Z's Commission report names staff, earned, clawback and net; `payable` and `shortfall`
 * are this module's, because a payroll file has to say what to pay. `payable` is `net` floored at
 * zero and `shortfall` is exactly what the floor removed, so the two carry the negative instead of
 * losing it: how a shortfall is recovered is a payroll policy the spec does not state.
 */
export function payrollRows(period, statements, columns) {
    if (period.status !== 'approved') {
        throw new CommissionError(`Period ${period.id} is ${period.status}; commission is payable only once approved`);
    }
    const headers = PAYROLL_FIELDS.map((f) => columns[f]);
    if (headers.some((h) => h === '') || new Set(headers).size !== headers.length) {
        throw new CommissionError('COM-009: every payroll field needs its own non-empty column name');
    }
    return statements.map((s) => {
        const value = {
            staff: s.staffId,
            period: s.periodId,
            currency: s.net.currency,
            earned: s.earned.minor.toString(),
            clawback: s.clawback.minor.toString(),
            remake_deduction: s.remakeDeduction.minor.toString(),
            metric_bonus: s.metricBonus.minor.toString(),
            net: s.net.minor.toString(),
            payable: (s.net.minor > 0n ? s.net.minor : 0n).toString(),
            shortfall: s.shortfall.minor.toString(),
        };
        const row = {};
        for (const f of PAYROLL_FIELDS)
            row[columns[f]] = value[f];
        return row;
    });
}
//# sourceMappingURL=commission.js.map